/**
 * Alberatura / Categorie — sezione riservata al Super Admin.
 *
 * Tre regole governano questa pagina, e vengono tutte da fuori:
 *
 * 1. **Le denominazioni non si digitano.** Si scelgono da catalogo ANAC c1,
 *    perche' sono normativamente vincolate e la difformita' e' rilevata
 *    automaticamente da TrasparenzAI. L'unica eccezione e' il ramo «Altri
 *    contenuti → Dati ulteriori», dove ANAC consente denominazioni libere.
 * 2. **Un nodo non applicabile non resta vuoto.** Disattivando l'applicabilita'
 *    il portale espone la dicitura prevista al posto del contenuto: e' un
 *    obbligo, non una scelta editoriale, e per questo la dicitura e' mostrata
 *    qui letteralmente, non parafrasata.
 * 3. **Ogni modifica rigenera la at_map.** L'operazione e' dichiarata prima
 *    della conferma, perche' cambia cio' che i crawler leggono dal sito.
 *
 * La scrittura e' comunque autorizzata dall'API: qui si evita solo di proporre
 * al redattore comandi che riceverebbero un 403.
 *
 * I DOCUMENTI GIA' CARICATI, E LA PUBBLICAZIONE
 * ---------------------------------------------
 * L'albero non mostra solo la struttura: sotto ogni nodo aperto elenca i
 * contenuti che ci sono dentro — bozze e pubblicazioni, con i loro documenti —
 * e da li' si comanda la transizione bozza → pubblicato.
 *
 * Sta qui e non in una pagina a parte per una ragione di dominio: cio' che si
 * pubblica e' il contenuto **di un obbligo**, e l'obbligo e' identificato dal
 * suo posto nell'albero. Fra i 219 nodi cinquantadue denominazioni si
 * ripetono, e per una coppia coincide anche la sottosezione madre: un elenco
 * piatto di titoli chiede all'operatore di ricordare su quale ramo sta
 * pubblicando, l'albero glielo mostra.
 *
 * Due cose che la pagina **non** fa, ed e' voluto:
 *
 * - **non carica file.** Il caricamento vive in «Documenti e cartelle», dove
 *   ci sono la zona di rilascio e le cartelle; qui si pubblica cio' che e' gia'
 *   stato caricato. Sono due gesti con conseguenze diverse — l'uno reversibile,
 *   l'altro no — e tenerli separati e' la ragione per cui la bozza esiste.
 * - **non decide se una bozza sia pubblicabile.** La regola sta in
 *   `lib/documenti.ts` e l'ultima parola e' dell'API: un controllo bloccante
 *   fallito ferma la pubblicazione lato server. Qui il pulsante si disabilita
 *   e **dichiara il motivo**, invece di offrire un comando che finirebbe in un
 *   errore o, peggio, di tacere sul perche' non risponde.
 *
 * La pubblicazione e' irreversibile per costruzione: crea una versione
 * immutabile, sposta l'alias «-latest» e rigenera la at_map. Per questo il
 * comando non e' un «Salva» e gli effetti sono dichiarati in pagina, non
 * scoperti dopo.
 *
 * PERCHE' NON E' UN `role="tree"`
 * -------------------------------
 * Decisione presa, non rinviata. Il pattern ARIA `tree`/`treeitem` porta con se'
 * un contratto di tastiera preciso — frecce su/giu' fra le righe, destra e
 * sinistra per aprire e chiudere, Home/End agli estremi, un solo punto di
 * tabulazione per l'intero albero con il fuoco che si sposta fra i `treeitem`.
 * Dichiarare i ruoli senza quel gestore annuncerebbe un widget che la tastiera
 * non offre: una regressione su WCAG 2.1.1, peggiore del silenzio.
 *
 * Implementarlo per intero non e' comunque la strada giusta *qui*, e non per
 * pigrizia: ogni riga di questo albero contiene piu' comandi — l'interruttore
 * di applicabilita', «Aggiungi obbligo», «Rimuovi». Un `treeitem` a fuoco unico
 * con controlli dentro e' il caso che il pattern non copre (per quello esiste il
 * `treegrid`, che pero' chiede una griglia bidimensionale che questa pagina non
 * ha): le frecce servirebbero all'albero e il Tab ai controlli, e i due usi si
 * pestano i piedi.
 *
 * Si resta percio' su una **struttura che non mente**: elenchi veri (`ul`/`li`,
 * gia' privi di marcatore per via del reset), che la navigazione con Tab
 * soddisfa alla lettera, con `aria-level` sulle righe discendenti a portare la
 * gerarchia. `aria-level` e' una proprieta' supportata da `listitem`, ed e'
 * proprio il modo previsto per esprimere un albero reso in forma appiattita —
 * che e' come questa pagina lo rende. Il rientro grafico, da solo, non e'
 * esposto ad alcuna API di accessibilita'.
 *
 * Il livello annunciato e' calcolato in `lib/alberatura.ts`
 * (`discendentiDiNodo`) e verificato in `verifiche/dominio.verifica.ts` sui 219
 * nodi del catalogo: e' la parte che si puo' provare senza un ambiente DOM, che
 * il progetto continua a non avere.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  aggiornaNodo,
  creaNodo,
  elencaPubblicazioni,
  eliminaNodo,
  ErroreApi,
  pubblica,
} from "../api/client";
import type { Nodo, Pubblicazione, RispostaPubblicazioneEseguita } from "../api/types";
import { CampoLibero, CampoScelta } from "../components/campi";
import { ContenutiNodo, RiepilogoContenuti } from "../components/ContenutiNodo";
import { Modale } from "../components/Modale";
import {
  Avviso,
  Bottone,
  Chip,
  Interruttore,
  Scheda,
  Scheletro,
} from "../components/primitive";
import { IconaChevron } from "../components/Icone";
import { useAlberatura } from "../dati/contesto-alberatura";
import { DICITURA_NON_APPLICABILE, RAMO_DENOMINAZIONI_LIBERE } from "../data/catalogo-nodi";
import { IntestazionePagina } from "../layout/Guscio";
import {
  catenePerId,
  comandiNodo,
  discendentiDiNodo,
  figliMancantiACatalogo,
  percorsoNodo,
} from "../lib/alberatura";
import { inAttesaDiPubblicazione } from "../lib/documenti";
import { appiattisciAlberatura } from "../lib/scadenze";
import { useRisorsa } from "../lib/useRisorsa";

/**
 * Le rotte di scrittura dell'alberatura esigono `?ente=`: senza, l'API risponde
 * 422. L'ente si legge dall'alberatura caricata; se manca, l'operazione non
 * parte affatto invece di andare a vuoto sul server.
 */
const MESSAGGIO_ENTE_MANCANTE =
  "L'ente di riferimento non e' ancora noto: rileggere l'alberatura e riprovare.";

/** Trasforma una denominazione libera nello slug stabile usato come `id_nodo`. */
function slug(denominazione: string): string {
  return denominazione
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60);
}

export function Alberatura() {
  const { ente, nodi, inCaricamento, errore, ricarica } = useAlberatura();
  const enteId = ente?.id ?? null;
  const [aperti, setAperti] = useState<Set<string>>(new Set());
  const [inLavorazione, setInLavorazione] = useState<string | null>(null);
  const [erroreOperazione, setErroreOperazione] = useState<string | null>(null);
  const [padreDaEstendere, setPadreDaEstendere] = useState<Nodo | null>(null);
  const [daConfermare, setDaConfermare] = useState<{
    nodo: Nodo;
    messaggio: string;
  } | null>(null);
  /** Bozza in corso di pubblicazione: l'attesa si accende su quel pulsante. */
  const [inPubblicazione, setInPubblicazione] = useState<number | null>(null);
  const [esitoPubblicazione, setEsitoPubblicazione] = useState<{
    titolo: string;
    esito: RispostaPubblicazioneEseguita;
  } | null>(null);

  useEffect(() => {
    document.title = "Alberatura · Amministrazione trasparente";
  }, []);

  /**
   * Contenuti dell'ente: bozze e pubblicazioni, con i documenti della versione
   * in lavorazione.
   *
   * Una richiesta sola per l'intero albero, non una per nodo: l'elenco e' quello
   * del perimetro di chi guarda, e 219 richieste — una per riga — sarebbero 219
   * modi di disegnare la pagina a meta'.
   *
   * **Non parte finche' l'ente non e' noto**, ed e' un requisito e non
   * un'ottimizzazione: `ente` omesso significa, per un Super Admin, le
   * pubblicazioni di *tutti* gli enti (`elenca_pubblicazioni` lo filtra solo se
   * il parametro c'e'), e l'indice che segue e' costruito su `id_nodo`, che e'
   * unico per ente e non nel sistema. L'alberatura carica in parallelo, quindi
   * al primo render `enteId` e' ancora nullo: senza questa guardia bozze di un
   * altro Comune comparivano sotto i nodi di questo, con il pulsante «Pubblica»
   * attivo sopra.
   */
  const contenuti = useRisorsa<Pubblicazione[]>(
    (segnale) =>
      enteId === null
        ? Promise.resolve([])
        : elencaPubblicazioni({ ente: enteId }, segnale),
    [enteId],
  );

  /** Pubblicazioni raggruppate per nodo: l'albero le cerca per identificativo. */
  const perNodo = useMemo(() => {
    const mappa = new Map<string, Pubblicazione[]>();
    for (const pubblicazione of contenuti.dato ?? []) {
      if (!pubblicazione.nodo_id) continue;
      const elenco = mappa.get(pubblicazione.nodo_id);
      if (elenco) elenco.push(pubblicazione);
      else mappa.set(pubblicazione.nodo_id, [pubblicazione]);
    }
    return mappa;
  }, [contenuti.dato]);

  const contenutiDi = useCallback(
    (idNodo: string): Pubblicazione[] => perNodo.get(idNodo) ?? [],
    [perNodo],
  );

  /**
   * Contenuti in attesa di pubblicazione nel sottoalbero di ciascuna sezione,
   * per la riga chiusa.
   *
   * Serve a una cosa sola, ed e' la ragione per cui esiste: su un ente popolato
   * i contenuti in attesa sono pochi e sparsi fra 219 righe. Senza un conteggio
   * sul ramo, trovarli vuole aprire l'albero sezione per sezione.
   *
   * Conta con la stessa regola con cui si mostra il pulsante
   * (`inAttesaDiPubblicazione`): bozze **e** revisioni aperte su contenuti gia'
   * pubblicati. Filtrare sul solo `stato === "bozza"` rendeva invisibili proprio
   * le revisioni, che sono il caso piu' comune su un ente in esercizio.
   *
   * Calcolato una volta per alberatura e non per riga: il conteggio attraversa
   * il sottoalbero, e rifarlo a ogni resa significherebbe attraversare i 219
   * nodi a ogni apertura di una sezione.
   */
  const attesaPerRamo = useMemo(() => {
    const conta = (nodo: Nodo): number =>
      [nodo, ...discendentiDiNodo(nodo).map(({ nodo: figlio }) => figlio)].reduce(
        (totale, voce) =>
          totale + (perNodo.get(voce.id_nodo) ?? []).filter(inAttesaDiPubblicazione).length,
        0,
      );
    return new Map(nodi.map((nodo) => [nodo.id_nodo, conta(nodo)] as const));
  }, [nodi, perNodo]);

  const attesaTotale = useMemo(
    () => (contenuti.dato ?? []).filter(inAttesaDiPubblicazione).length,
    [contenuti.dato],
  );

  /**
   * Transizione bozza → pubblicato.
   *
   * Dopo l'esito si rileggono **entrambe** le fonti: i contenuti, perche' la
   * bozza ora e' una pubblicazione con una versione in piu', e l'alberatura,
   * perche' la pubblicazione ricalcola la data di ultimo aggiornamento del nodo
   * che la riga mostra.
   */
  const pubblicaBozza = async (pubblicazione: Pubblicazione) => {
    setErroreOperazione(null);
    setEsitoPubblicazione(null);
    setInPubblicazione(pubblicazione.id);
    try {
      const esito = await pubblica(pubblicazione.id);
      setEsitoPubblicazione({ titolo: pubblicazione.titolo, esito });
      contenuti.ricarica();
      ricarica();
    } catch (causa) {
      setErroreOperazione(
        causa instanceof ErroreApi
          ? causa.messaggioEsteso
          : "Non e' stato possibile pubblicare la bozza.",
      );
    } finally {
      setInPubblicazione(null);
    }
  };

  const commutaApertura = (idNodo: string) => {
    setAperti((correnti) => {
      const successivi = new Set(correnti);
      if (successivi.has(idNodo)) successivi.delete(idNodo);
      else successivi.add(idNodo);
      return successivi;
    });
  };

  const commutaApplicabilita = async (nodo: Nodo, applicabile: boolean) => {
    setErroreOperazione(null);
    if (enteId === null) {
      setErroreOperazione(MESSAGGIO_ENTE_MANCANTE);
      return;
    }
    setInLavorazione(nodo.id_nodo);
    try {
      await aggiornaNodo(nodo.id_nodo, { applicabile }, enteId);
      ricarica();
    } catch (causa) {
      setErroreOperazione(
        causa instanceof ErroreApi
          ? causa.messaggioEsteso
          : "Non e' stato possibile aggiornare il nodo.",
      );
    } finally {
      setInLavorazione(null);
    }
  };

  /**
   * Rimozione di un nodo, con la via d'uscita per le voci a catalogo.
   *
   * Su una voce ANAC l'API risponde 409 e insegna che la strada giusta e'
   * dichiararla non applicabile. Mostriamo quel messaggio testualmente — non lo
   * parafrasiamo: e' la spiegazione normativa — e solo dopo offriamo la
   * conferma. Senza questo secondo passo il comando «Rimuovi», offerto su quasi
   * tutti i nodi di un ente popolato, fallirebbe sempre senza via d'uscita.
   */
  const rimuovi = async (nodo: Nodo, confermaCatalogo = false) => {
    setErroreOperazione(null);
    if (enteId === null) {
      setErroreOperazione(MESSAGGIO_ENTE_MANCANTE);
      return;
    }
    setInLavorazione(nodo.id_nodo);
    try {
      await eliminaNodo(nodo.id_nodo, enteId, confermaCatalogo);
      setDaConfermare(null);
      ricarica();
    } catch (causa) {
      if (
        causa instanceof ErroreApi &&
        causa.codice === "RIMOZIONE_NODO_A_CATALOGO" &&
        !confermaCatalogo
      ) {
        setDaConfermare({ nodo, messaggio: causa.message });
      } else {
        setDaConfermare(null);
        setErroreOperazione(
          causa instanceof ErroreApi
            ? causa.messaggioEsteso
            : "Non e' stato possibile rimuovere il nodo.",
        );
      }
    } finally {
      setInLavorazione(null);
    }
  };

  const nonApplicabili = appiattisciAlberatura(nodi).filter((nodo) => !nodo.applicabile);

  // I comandi si ripetono su 219 righe con lo stesso nome: «Rimuovi»,
  // «Applicabile». Il percorso completo e' cio' che li rende distinguibili a
  // chi legge la pagina per elenco dei controlli — il rientro di 20 px non e'
  // esposto ad alcuna API di accessibilita', e fra gli obblighi ci sono
  // denominazioni identiche su rami diversi.
  const catene = useMemo(() => catenePerId(nodi), [nodi]);
  const percorso = (nodo: Nodo) => percorsoNodo(catene, nodo.id_nodo);

  return (
    <div className="pila">
      <IntestazionePagina
        sopratitolo="Amministrazione"
        titolo="Alberatura / Categorie"
        sottotitolo="Alberatura ANAC c1 su tre livelli: sezioni, sottosezioni e obblighi. Le denominazioni si scelgono da catalogo: non sono digitabili. Aprendo un nodo si vedono i documenti che contiene, e da qui si pubblicano le bozze."
        azioni={
          <Bottone
            variante="contorno"
            onClick={() => {
              ricarica();
              contenuti.ricarica();
            }}
          >
            Rileggi l'alberatura
          </Bottone>
        }
      />

      {errore ? (
        <Avviso tono="critico" titolo="Alberatura non disponibile">
          {errore.messaggioEsteso}
        </Avviso>
      ) : null}
      {contenuti.errore ? (
        <Avviso tono="critico" titolo="Contenuti non disponibili">
          {contenuti.errore.messaggioEsteso} L'alberatura resta modificabile: cio' che manca sono
          i documenti mostrati sotto i nodi.
        </Avviso>
      ) : null}
      {erroreOperazione ? (
        <Avviso tono="critico" titolo="Operazione non riuscita">
          {erroreOperazione}
        </Avviso>
      ) : null}

      {/* Esito della pubblicazione. La at_map e' la parte che non si vede: se
          non si e' rigenerata, la divergenza fra mappa e alberatura e'
          rilevabile automaticamente da chiunque, e va detto subito. */}
      {esitoPubblicazione ? (
        <Avviso
          tono={esitoPubblicazione.esito.at_map_rigenerata ? "positivo" : "attenzione"}
          titolo={`«${esitoPubblicazione.titolo}» e' pubblicata`}
        >
          Versione n. {esitoPubblicazione.esito.versione.progressivo} del{" "}
          {esitoPubblicazione.esito.versione.data_prima_pubblicazione}, ultima modifica{" "}
          {esitoPubblicazione.esito.versione.data_ultima_modifica}.{" "}
          {esitoPubblicazione.esito.at_map_rigenerata
            ? "La at_map e' stata rigenerata."
            : "La at_map non risulta rigenerata: verificare lo stato nel pannello di conformita'."}
        </Avviso>
      ) : null}

      <Avviso tono="primario" titolo="Effetti delle modifiche">
        Ogni modifica all'alberatura o agli indirizzi comporta la rigenerazione della at_map e la
        sua rivalidazione contro lo schema ANAC v1.0.0. Gli identificativi dei nodi sono stabili per
        contratto e non vanno cambiati dopo il primo rilascio.
      </Avviso>

      {attesaTotale > 0 ? (
        <Avviso
          tono="attenzione"
          titolo={`${attesaTotale} ${attesaTotale === 1 ? "contenuto" : "contenuti"} in attesa di pubblicazione`}
        >
          Bozze e revisioni di contenuti gia' pubblicati: in entrambi i casi i documenti sono
          caricati ma non ancora pubblici, e nessuno di essi compare sul portale finche' non si
          pubblica. Le righe che li contengono portano il conteggio del ramo.
        </Avviso>
      ) : null}

      {nonApplicabili.length > 0 ? (
        <Avviso tono="attenzione" titolo={`${nonApplicabili.length} nodi non applicabili`}>
          Sui nodi disattivati il portale pubblica automaticamente la dicitura: «
          {DICITURA_NON_APPLICABILE}». Il nodo resta presente nell'alberatura e nella at_map.
        </Avviso>
      ) : null}

      <Scheda
        titolo={`Alberatura (${nodi.length} nodi di primo livello)`}
        descrizione={
          contenuti.inCaricamento
            ? "Lettura dei contenuti in corso: i documenti compaiono sotto i nodi appena arrivano."
            : undefined
        }
        fitto
      >
        {inCaricamento ? (
          <div style={{ padding: "1.25rem" }}>
            <Scheletro righe={8} />
          </div>
        ) : (
          <ul className="albero">
            {nodi.map((nodo) => (
              <li
                key={nodo.id_nodo}
                className={`albero__nodo${nodo.applicabile ? "" : " albero__nodo--non-applicabile"}`}
                aria-level={1}
              >
                <div className="albero__riga">
                  <button
                    type="button"
                    className="albero__interruttore-apertura"
                    onClick={() => commutaApertura(nodo.id_nodo)}
                    aria-expanded={aperti.has(nodo.id_nodo)}
                    aria-controls={`albero-figli-${nodo.id_nodo}`}
                    aria-label={`${aperti.has(nodo.id_nodo) ? "Chiudi" : "Apri"} ${nodo.denominazione}`}
                  >
                    <IconaChevron
                      style={{
                        transform: aperti.has(nodo.id_nodo) ? "rotate(90deg)" : "none",
                        transition: "transform 140ms",
                      }}
                    />
                  </button>

                  <div className="albero__corpo">
                    <div className="albero__denominazione">{nodo.denominazione}</div>
                    <div className="albero__meta">
                      {nodo.id_nodo} · {nodo.riferimento_normativo}
                    </div>
                  </div>

                  <div className="riga">
                    <Chip tono="neutro" pallino={false}>
                      {discendentiDiNodo(nodo).length} voci
                    </Chip>
                    {/* Il conteggio sta sulla riga chiusa: e' l'unico modo di
                        sapere che dentro c'e' qualcosa da pubblicare senza
                        aprire tutte le sezioni. */}
                    {(attesaPerRamo.get(nodo.id_nodo) ?? 0) > 0 ? (
                      <Chip tono="attenzione">
                        {attesaPerRamo.get(nodo.id_nodo)} in attesa
                      </Chip>
                    ) : null}
                    {nodo.data_ultimo_aggiornamento ? (
                      <span className="dato testo-fioco">{nodo.data_ultimo_aggiornamento}</span>
                    ) : null}
                    <Interruttore
                      acceso={nodo.applicabile}
                      etichetta="Applicabile"
                      contesto={percorso(nodo)}
                      disabilitato={inLavorazione === nodo.id_nodo}
                      onCambia={(valore) => void commutaApplicabilita(nodo, valore)}
                    />
                    {comandiNodo(nodo).puoEstendere ? (
                      <Bottone
                        variante="contorno"
                        dimensione="piccolo"
                        onClick={() => setPadreDaEstendere(nodo)}
                        aria-label={`${comandiNodo(nodo).etichettaEstensione} a ${percorso(nodo)}`}
                      >
                        {comandiNodo(nodo).etichettaEstensione}
                      </Bottone>
                    ) : null}
                  </div>
                </div>

                {!nodo.applicabile ? (
                  <div className="albero__riga albero__riga--figlio">
                    <span className="campo__aiuto">
                      Dicitura esposta sul portale: «{DICITURA_NON_APPLICABILE}».
                    </span>
                  </div>
                ) : null}

                {/* Un contenuto appeso direttamente alla sezione e' raro ma
                    possibile: si mostra dov'e', non si lascia invisibile. */}
                <ContenutiNodo
                  pubblicazioni={contenutiDi(nodo.id_nodo)}
                  percorso={percorso(nodo)}
                  inPubblicazione={inPubblicazione}
                  onPubblica={(pubblicazione) => void pubblicaBozza(pubblicazione)}
                />

                {/* Il contenitore esiste anche da chiuso: `aria-controls` del
                    pulsante di apertura deve puntare a un elemento presente.
                    E' un elenco vero e non un `role="group"`: cosi' il numero
                    delle voci viene annunciato, e `aria-level` sulle righe e'
                    valido perche' sono `listitem`. */}
                <ul
                  id={`albero-figli-${nodo.id_nodo}`}
                  className="albero__discendenti"
                  aria-label={`Sottosezioni e obblighi di ${nodo.denominazione}`}
                >
                  {aperti.has(nodo.id_nodo)
                    ? discendentiDiNodo(nodo).map(({ nodo: figlio, profondita, livelloAria }) => (
                        /* La voce non e' piu' la sola riga: sotto i comandi ci
                           stanno i contenuti del nodo. La riga resta un flex a
                           se' — dentro l'elemento di elenco, non al posto suo —
                           cosi' `aria-level` continua a stare su un `listitem`
                           e il rientro grafico non si perde. */
                        <li
                          key={figlio.id_nodo}
                          className="albero__discendente"
                          aria-level={livelloAria}
                        >
                          <div
                            className="albero__riga albero__riga--figlio"
                            style={{ paddingLeft: `${profondita * 1.25}rem` }}
                          >
                            <div className="albero__corpo">
                              <div className="albero__denominazione">{figlio.denominazione}</div>
                              <div className="albero__meta">
                                {figlio.id_nodo}
                                {figlio.url ? ` · ${figlio.url}` : ""}
                              </div>
                              {!figlio.applicabile ? (
                                <div className="campo__aiuto">
                                  Dicitura esposta: «{DICITURA_NON_APPLICABILE}».
                                </div>
                              ) : null}
                            </div>
                            <RiepilogoContenuti pubblicazioni={contenutiDi(figlio.id_nodo)} />
                            <ComandiRiga
                              nodo={figlio}
                              percorso={percorso(figlio)}
                              inLavorazione={inLavorazione === figlio.id_nodo}
                              onApplicabilita={(valore) =>
                                void commutaApplicabilita(figlio, valore)
                              }
                              onEstendi={() => setPadreDaEstendere(figlio)}
                              onRimuovi={() => void rimuovi(figlio)}
                            />
                          </div>
                          <ContenutiNodo
                            pubblicazioni={contenutiDi(figlio.id_nodo)}
                            percorso={percorso(figlio)}
                            rientro={profondita * 1.25}
                            inPubblicazione={inPubblicazione}
                            onPubblica={(pubblicazione) => void pubblicaBozza(pubblicazione)}
                          />
                        </li>
                      ))
                    : null}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </Scheda>

      {/* La modale si monta e si smonta: lo stato del modulo nasce con lei. */}
      {padreDaEstendere ? (
        <ModaleNuovaSottosezione
          key={padreDaEstendere.id_nodo}
          padre={padreDaEstendere}
          enteId={enteId}
          onChiudi={() => setPadreDaEstendere(null)}
          onCreata={() => {
            setPadreDaEstendere(null);
            ricarica();
          }}
        />
      ) : null}

      {/* Rimozione di una voce a catalogo: il messaggio dell'API si ripete alla
          lettera, perche' e' la spiegazione normativa di perche' la strada
          giusta sia un'altra. La conferma resta possibile, ma dichiarata. */}
      {daConfermare ? (
        <Modale
          titolo="Rimuovere una voce dell'albero ANAC?"
          aperta
          onChiudi={() => setDaConfermare(null)}
          pedice={
            <>
              <Bottone variante="contorno" onClick={() => setDaConfermare(null)}>
                Annulla
              </Bottone>
              <Bottone
                onClick={() => void rimuovi(daConfermare.nodo, true)}
                disabled={inLavorazione === daConfermare.nodo.id_nodo}
              >
                Rimuovi comunque
              </Bottone>
            </>
          }
        >
          <p>{daConfermare.messaggio}</p>
          <p className="campo__aiuto">
            La rimozione ha senso solo per un nodo creato per errore su un ente non
            ancora in linea. Su un ente pubblicato toglie la voce dalla at_map, e la
            sezione risulta incompleta alle verifiche automatiche.
          </p>
        </Modale>
      ) : null}
    </div>
  );
}

/**
 * Comandi di una riga discendente.
 *
 * I due comandi **non si escludono**: legarli l'uno all'altro — chi ha figli si
 * puo' solo estendere, chi non ne ha si puo' solo rimuovere — lasciava una
 * sottosezione svuotata dei suoi obblighi senza alcun modo di ricrearli, e il
 * back-office non ha un comando di ripopolamento dal catalogo. Le 54
 * sottosezioni con un solo obbligo distavano due clic da quello stato.
 */
function ComandiRiga({
  nodo,
  percorso,
  inLavorazione,
  onApplicabilita,
  onEstendi,
  onRimuovi,
}: {
  nodo: Nodo;
  percorso: string;
  inLavorazione: boolean;
  onApplicabilita: (valore: boolean) => void;
  onEstendi: () => void;
  onRimuovi: () => void;
}) {
  const comandi = comandiNodo(nodo);
  return (
    <div className="riga">
      <Interruttore
        acceso={nodo.applicabile}
        etichetta="Applicabile"
        contesto={percorso}
        disabilitato={inLavorazione}
        onCambia={onApplicabilita}
      />
      {comandi.puoEstendere ? (
        <Bottone
          variante="contorno"
          dimensione="piccolo"
          onClick={onEstendi}
          aria-label={`${comandi.etichettaEstensione} a ${percorso}`}
        >
          {comandi.etichettaEstensione}
        </Bottone>
      ) : null}
      {comandi.puoRimuovere ? (
        <Bottone
          variante="fantasma"
          dimensione="piccolo"
          onClick={onRimuovi}
          disabled={inLavorazione}
          aria-label={`Rimuovi ${percorso}`}
        >
          Rimuovi
        </Bottone>
      ) : null}
    </div>
  );
}

/**
 * Creazione di una sottosezione.
 *
 * La denominazione e' una select sul catalogo ANAC; il campo libero compare solo
 * sotto «Altri contenuti → Dati ulteriori» (`RAMO_DENOMINAZIONI_LIBERE`), l'unico
 * ramo in cui la nomenclatura non e' vincolata. E' la sottosezione e non la
 * sezione: e' li' che l'API accetta un nome fuori catalogo, e offrirlo altrove
 * significa far compilare la modale per poi restituire un 422.
 *
 * RIPOPOLAMENTO DAL CATALOGO
 * --------------------------
 * Accanto alla creazione singola c'e' un comando che ricrea **tutte** le voci
 * di catalogo mancanti sotto il nodo. Serve a chiudere il vicolo cieco della
 * sottosezione svuotata: rimetterci dieci obblighi uno per uno significa dieci
 * giri di modale.
 *
 * Non passa da un endpoint di seed — che non esiste nel client e che
 * rigenererebbe l'albero intero, toccando indirizzi gia' pubblicati — ma ripete
 * la stessa `POST /api/v1/nodi` che farebbe l'operatore a mano, una voce alla
 * volta e in ordine di catalogo. Il costo e' N richieste; il guadagno e' che
 * ogni creazione resta la stessa operazione autorizzata e verificata di prima,
 * e non c'e' una seconda strada di scrittura da tenere in sicurezza.
 *
 * Alla prima che fallisce si ferma e dichiara quante ne ha create: un'operazione
 * di massa che tace su cio' che ha fatto a meta' e' peggio di una che non parte.
 */
function ModaleNuovaSottosezione({
  padre,
  enteId,
  onChiudi,
  onCreata,
}: {
  padre: Nodo;
  enteId: number | null;
  onChiudi: () => void;
  onCreata: () => void;
}) {
  const [scelta, setScelta] = useState("");
  const [denominazioneLibera, setDenominazioneLibera] = useState("");
  const [errore, setErrore] = useState<string | null>(null);
  /**
   * Quale delle due scritture e' in corso.
   *
   * Non un booleano: i pulsanti sono due, e l'indicatore di attesa deve accendersi
   * su quello che si e' premuto. Con un solo `inCorso` il ripopolamento faceva
   * lampeggiare «Attendere…» sul pulsante di creazione singola.
   */
  const [operazione, setOperazione] = useState<null | "singola" | "ripopolamento">(null);
  const inCorso = operazione !== null;

  // Cio' che si crea sotto una sottosezione e' un obbligo, non una
  // sottosezione: chiamarlo col nome del livello sbagliato e' la meta' della
  // confusione, l'altra meta' era il pulsante che non c'era.
  const nomeFiglio = padre.livello === 1 ? "sottosezione" : "obbligo";
  const ramoLibero = padre.id_nodo === RAMO_DENOMINAZIONI_LIBERE;
  const disponibili = useMemo(
    () => figliMancantiACatalogo(padre.id_nodo, padre.figli ?? []),
    [padre],
  );

  const opzioni = [
    ...disponibili.map((voce) => ({ valore: voce.id_nodo, etichetta: voce.denominazione })),
    ...(ramoLibero
      ? [{ valore: "__libera__", etichetta: "Altra denominazione (fuori catalogo)" }]
      : []),
  ];

  const usaLibera = scelta === "__libera__";
  const voceCatalogo = disponibili.find((voce) => voce.id_nodo === scelta);

  const conferma = async () => {
    setErrore(null);
    if (enteId === null) {
      setErrore(MESSAGGIO_ENTE_MANCANTE);
      return;
    }
    setOperazione("singola");
    try {
      if (usaLibera) {
        // Ramo libero: la denominazione la digita l'operatore, quindi va inviata.
        const denominazione = denominazioneLibera.trim();
        await creaNodo({
          ente_id: enteId,
          id_nodo: slug(denominazione),
          id_padre: padre.id_nodo,
          denominazione,
          riferimento_normativo: "d.lgs. 33/2013, art. 7-bis, c. 3",
          applicabile: true,
        });
      } else if (voceCatalogo) {
        // Nodo a catalogo: denominazione e riferimento normativo li mette l'API
        // dal catalogo ANAC, che e' la sorgente unica. Inviarli qui aggiungerebbe
        // una seconda copia da tenere allineata.
        await creaNodo({
          ente_id: enteId,
          id_nodo: voceCatalogo.id_nodo,
          id_padre: padre.id_nodo,
          applicabile: true,
        });
      }
      onCreata();
    } catch (causa) {
      setErrore(
        causa instanceof ErroreApi
          ? causa.messaggioEsteso
          : nomeFiglio === "obbligo"
            ? "Non e' stato possibile creare l'obbligo."
            : "Non e' stato possibile creare la sottosezione.",
      );
    } finally {
      setOperazione(null);
    }
  };

  /**
   * Ricrea in blocco tutte le voci di catalogo mancanti sotto il nodo.
   *
   * Sequenziale, non in parallelo: ogni creazione rigenera la at_map, e N
   * rigenerazioni concorrenti sullo stesso albero non sono una gara che valga
   * la pena correre per guadagnare qualche decimo di secondo.
   */
  const ripopola = async () => {
    setErrore(null);
    if (enteId === null) {
      setErrore(MESSAGGIO_ENTE_MANCANTE);
      return;
    }
    setOperazione("ripopolamento");
    let create = 0;
    try {
      for (const voce of disponibili) {
        await creaNodo({
          ente_id: enteId,
          id_nodo: voce.id_nodo,
          id_padre: padre.id_nodo,
          applicabile: true,
        });
        create += 1;
      }
      onCreata();
    } catch (causa) {
      const motivo =
        causa instanceof ErroreApi
          ? causa.messaggioEsteso
          : "Non e' stato possibile completare il ripopolamento.";
      setErrore(
        create === 0
          ? motivo
          : `${motivo} Le prime ${create} voci su ${disponibili.length} sono state create: rilegga l'alberatura prima di riprovare, il comando ricreerebbe solo cio' che manca ancora.`,
      );
    } finally {
      setOperazione(null);
    }
  };

  const pronto = usaLibera ? denominazioneLibera.trim().length > 2 : Boolean(voceCatalogo);

  return (
    <Modale
      titolo={
        nomeFiglio === "obbligo"
          ? `Nuovo obbligo di «${padre.denominazione}»`
          : `Nuova sottosezione di «${padre.denominazione}»`
      }
      aperta
      onChiudi={onChiudi}
      pedice={
        <>
          <Bottone variante="fantasma" onClick={onChiudi}>
            Annulla
          </Bottone>
          {disponibili.length > 1 ? (
            <Bottone
              variante="contorno"
              onClick={() => void ripopola()}
              disabled={inCorso}
              inCorso={operazione === "ripopolamento"}
              aria-label={`Ripopola «${padre.denominazione}» creando le ${disponibili.length} voci di catalogo mancanti`}
            >
              Ripopola dal catalogo ({disponibili.length})
            </Bottone>
          ) : null}
          <Bottone
            variante="primario"
            onClick={conferma}
            disabled={!pronto || inCorso}
            inCorso={operazione === "singola"}
          >
            Crea e rigenera la at_map
          </Bottone>
        </>
      }
    >
      <div className="pila">
        {errore ? (
          <Avviso tono="critico" titolo="Creazione non riuscita">
            {errore}
          </Avviso>
        ) : null}

        {disponibili.length > 1 ? (
          <Avviso tono="attenzione" titolo="Ripopolamento dal catalogo">
            «Ripopola dal catalogo» crea in un colpo solo le {disponibili.length} voci previste
            dall'albero ANAC e non ancora presenti, una richiesta per voce e in ordine di catalogo.
            Ogni creazione rigenera la at_map e aggiunge un indirizzo pubblico; nessun indirizzo
            gia' pubblicato viene toccato.
          </Avviso>
        ) : null}

        {opzioni.length === 0 ? (
          <Avviso tono="positivo" titolo="Catalogo esaurito">
            {nomeFiglio === "obbligo"
              ? "Tutti gli obblighi previsti dall'albero ANAC per questa sottosezione sono gia' presenti."
              : "Tutte le sottosezioni previste dall'albero ANAC per questo nodo sono gia' presenti."}
          </Avviso>
        ) : (
          <CampoScelta
            etichetta="Denominazione"
            valore={scelta}
            onCambia={setScelta}
            opzioni={opzioni}
            obbligatorio
            aiuto={
              ramoLibero
                ? "«Dati ulteriori» e' l'unico ramo in cui ANAC consente denominazioni libere: qui la denominazione si puo' scrivere, ovunque altrove si sceglie."
                : "Le denominazioni provengono dal catalogo ANAC c1 e non sono modificabili: la difformita' e' rilevata automaticamente."
            }
          />
        )}

        {usaLibera ? (
          <CampoLibero
            etichetta="Denominazione libera"
            valore={denominazioneLibera}
            onCambia={setDenominazioneLibera}
            obbligatorio
            segnaposto="Dati ulteriori — Albo dei beneficiari"
            aiuto={
              denominazioneLibera.trim()
                ? `Identificativo che verra' assegnato: ${slug(denominazioneLibera) || "—"}`
                : "L'identificativo viene derivato dalla denominazione ed e' stabile per sempre."
            }
          />
        ) : null}

        {voceCatalogo ? (
          <div>
            <p className="anteprima__etichetta">Riferimento normativo</p>
            <p className="campo__aiuto">{voceCatalogo.riferimento_normativo}</p>
            <p className="anteprima__etichetta" style={{ marginTop: "0.6rem" }}>
              Identificativo
            </p>
            <p className="dato">{voceCatalogo.id_nodo}</p>
          </div>
        ) : null}
      </div>
    </Modale>
  );
}
